import type { AuthSession, AuthState } from '@updater/auth';

import {
  setGuestAuthState,
  subscribeGuestAuth,
  getGuestSession,
} from './guest-auth-store';

/**
 * Deliberately not the `session` key used by `persistAuth` — the guest session
 * shares no storage with the signed-in session.
 */
const STORAGE_KEY = 'guest-session';

const sessionKeys: (keyof AuthSession)[] = ['access-token', 'client', 'uid'];

/**
 * `sessionStorage`, not `localStorage` + cookies: a public session is scoped to
 * the tab that minted it. Writing it to a `.updater.com` cookie would leak it to
 * every other Updater app on the domain.
 */
function storage(): Storage | undefined {
  try {
    return window.sessionStorage;
  } catch {
    // Storage can throw outright when the browser blocks site data.
    return undefined;
  }
}

function readStoredState(): AuthState | undefined {
  try {
    const raw = storage()?.getItem(STORAGE_KEY);
    if (!raw) return undefined;

    const parsed = JSON.parse(raw) as Partial<AuthState> | null;
    // Only anonymous sessions belong in the guest store. Anything else is
    // either corrupt or written by an older build — discard it.
    if (parsed?.mode !== 'anonymous') return undefined;

    const session = parsed.session;
    if (!session || sessionKeys.some((key) => !session[key])) return undefined;

    return { mode: 'anonymous', session };
  } catch {
    return undefined;
  }
}

/**
 * Rehydrates a previously minted guest session and keeps it in sync, so a
 * reload mid buy flow reuses the same public session rather than minting a
 * second one. Call once, client side, alongside `persistAuth()`.
 */
export function persistGuestAuth(): void {
  if (typeof window === 'undefined') return;

  const stored = readStoredState();
  if (stored) setGuestAuthState(stored);

  // Subscribed after hydrating so the rehydrated value isn't written straight back.
  subscribeGuestAuth((state) => {
    const store = storage();
    if (!store) return;

    try {
      if (state.session) {
        store.setItem(STORAGE_KEY, JSON.stringify(state));
      } else {
        store.removeItem(STORAGE_KEY);
      }
    } catch {
      // A failed write only costs us the session on reload — never break the flow.
    }
  });
}

/** True when a guest session has already been minted for this tab. */
export function hasGuestSession(): boolean {
  return Boolean(getGuestSession());
}
